import { NonEmptyArray } from 'fp-ts/NonEmptyArray';
import { Banner, Icon, IconButton, IconName, Stack, Title } from '@optimus-web/core';
import { lt, showSuccessAlert } from '@mews/framework';
import { RateGroupOverview, useRepositoryQuery } from '@mews-commander/data-access';
import { TabbedContent } from '@mews-commander/ui';
import { useCurrentEnterpriseId, usePrivilege } from '@mews-commander/core';
import { ModalSidePanel } from '../../../../components';
import { useRateGroupGet } from '../../queries';
import { CurrentRateGroupContextProvider, useCurrentRateGroupContext } from '../../contexts';
import {
    useDeleteRateGroupConfirmationDialog,
    useEditDefaultPolicyDialogForm,
    useSettlementConfigurationEditDialogForm,
} from '../forms';
import { RateGroupTabValues, WithDefaultPolicy } from '../../types';
import { usePolicyExceptionUpdate, useSettlementConfigurationUpdate } from '../../mutations';
import { RatesTabContent } from './RatesTabContent';
import { GeneralTabContent } from './GeneralTabContent/GeneralTabContent';
import { PoliciesTabContent } from './PoliciesTabContent';

const RATE_GROUP_TABS: NonEmptyArray<string> = ['general', 'policies', 'rates'];

const EditDefaultPolicyButton = ({ defaultPolicy }: WithDefaultPolicy) => {
    const { mutateAsync } = usePolicyExceptionUpdate({
        invalidateQueries: {
            rateGroups: ['getAll', 'getOverviewEntries'],
        },
    });

    const [renderDialog, toggleDialog] = useEditDefaultPolicyDialogForm({
        defaultPolicy,
        onSubmit: async (values) =>
            mutateAsync(values, {
                onSuccess: () => {
                    showSuccessAlert({ title: lt('RateGroupUpdatedSuccessMessage') });
                },
            }),
    });

    return (
        <>
            <IconButton icon={IconName.Edit} size="small" onClick={toggleDialog} type="submit" />
            {renderDialog()}
        </>
    );
};

const EditPaymentConfigurationButton = () => {
    const rateGroup = useCurrentRateGroupContext();

    const { mutateAsync } = useSettlementConfigurationUpdate(rateGroup.id, {
        invalidateQueries: {
            rateGroups: ['getAll', 'getOverviewEntries'],
        },
    });

    const [renderDialog, toggleDialog] = useSettlementConfigurationEditDialogForm({
        ...rateGroup,
        title: lt('SettlementConfigurationEdit'),
        onSubmit: async (formValues) =>
            mutateAsync(formValues, {
                onSuccess: () => {
                    showSuccessAlert({ title: lt('RateGroupUpdatedSuccessMessage') });
                },
            }),
    });

    return (
        <>
            <IconButton icon={IconName.Edit} onClick={toggleDialog} size="small" type="submit" />
            {renderDialog()}
        </>
    );
};

interface Props {
    initialTab?: RateGroupTabValues;
    rateGroup?: RateGroupOverview;
    handleCloseSidePanel: () => void;
}

const HeaderActions = ({
    rateGroupId,
    handleCloseSidePanel,
}: {
    rateGroupId: string;
    handleCloseSidePanel: () => void;
}) => {
    const { confirmationDialog, openConfirmationDialog } = useDeleteRateGroupConfirmationDialog({
        handleCloseSidePanel,
        rateGroupId,
    });
    const canManageRateGroups = usePrivilege('manageRateGroups');

    return canManageRateGroups ? (
        <>
            <IconButton icon={IconName.Delete} onClick={openConfirmationDialog} size="small" type="submit" />
            {confirmationDialog}
        </>
    ) : null;
};

export const RateGroupSidePanel = ({ initialTab, rateGroup, handleCloseSidePanel }: Props) => {
    const enterpriseId = useCurrentEnterpriseId();

    const { data: rateGroupData, isFetching } = useRateGroupGet(rateGroup?.id);
    const { data: ratesData, isFetching: isFetchingRates } = useRepositoryQuery(
        'rates',
        'getAll',
        {
            enterpriseIds: [enterpriseId],
        },
        { staleTime: 5000, select: ({ rates }) => rates }
    );
    const canManageRateGroups = usePrivilege('manageRateGroups');

    return (
        <ModalSidePanel
            content={
                <Stack vertical spacing="400">
                    {rateGroup?.isPortfolioManaged ? (
                        <Banner
                            variant="info"
                            primaryText={lt('LinkedRateGroupLimitedEditingBannerTitle')}
                            secondaryText={lt('LinkedRateGroupLimitedEditingBannerDescriptionSingleProperty')}
                        />
                    ) : null}
                    <TabbedContent
                        tabs={RATE_GROUP_TABS}
                        tabLabels={{
                            general: lt('General'),
                            policies: lt('GeneralPolicyPlural'),
                            rates: lt('RatePlural'),
                        }}
                        initialTab={initialTab}
                        inline
                        showDivider
                    >
                        {({ matchTabs }) => {
                            if (!(rateGroup && rateGroupData && ratesData)) {
                                return null;
                            }

                            return (
                                <CurrentRateGroupContextProvider
                                    value={{
                                        ...rateGroup,
                                        ...rateGroupData,
                                        rates: ratesData
                                            ? ratesData.filter((rate) => rate.rateGroupId === rateGroup.id)
                                            : [],
                                    }}
                                >
                                    {matchTabs({
                                        general: () => <GeneralTabContent />,
                                        policies: () => (
                                            <PoliciesTabContent
                                                editPaymentConfigurationButton={
                                                    canManageRateGroups ? <EditPaymentConfigurationButton /> : null
                                                }
                                                editDefaultPolicyButton={
                                                    canManageRateGroups && rateGroupData.defaultPolicy ? (
                                                        <EditDefaultPolicyButton
                                                            defaultPolicy={rateGroupData.defaultPolicy}
                                                        />
                                                    ) : null
                                                }
                                                policyExceptionsEditable={canManageRateGroups}
                                            />
                                        ),
                                        rates: () => <RatesTabContent rateGroupId={rateGroup.id} />,
                                    })}
                                </CurrentRateGroupContextProvider>
                            );
                        }}
                    </TabbedContent>
                </Stack>
            }
            headerActions={
                rateGroup && !rateGroup.isPortfolioManaged ? (
                    <HeaderActions rateGroupId={rateGroup.id} handleCloseSidePanel={handleCloseSidePanel} />
                ) : null
            }
            loading={isFetching || isFetchingRates}
            onClose={() => {
                handleCloseSidePanel();
            }}
            opened={!!rateGroup}
            title={
                rateGroup ? (
                    <Stack itemAlignment="center" spacing="100">
                        <Title level={2} wordBreak="break-all" ellipsis maxLines={2}>
                            {rateGroup.name}
                        </Title>
                        {rateGroup.isPortfolioManaged ? <Icon value={IconName.CustomLink} /> : null}
                    </Stack>
                ) : null
            }
            width="medium"
        />
    );
};
